import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaCheck, FaChevronDown, FaInfoCircle, FaTimes } from 'react-icons/fa';
import {
  CLUSTER_PAIRING_NUDGES,
  MIN_CLUSTERS_SMALL,
  getClustersAvailableForTier,
  getDefaultClustersForTier,
  normalizeGarageTier,
  resolveFuelSpecialistTypes,
} from '../../constants/garageServiceClusters';

function normalizeIds(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw
    .map(Number)
    .filter((n) => n >= 1 && n <= 10 && !seen.has(n) && (seen.add(n) || true))
    .sort((a, b) => a - b);
}

export default function GarageClusterSelector({
  tier,
  selectedClusters = [],
  fuelSpecialistTypes = [],
  onChange,
  hasError = false,
  errorMessage = '',
}) {
  const canonical = normalizeGarageTier(tier);
  const selected = normalizeIds(selectedClusters);
  const [open, setOpen] = useState(false);
  const [dismissedNudges, setDismissedNudges] = useState(() => new Set());
  const wrapRef = useRef(null);

  const available = useMemo(() => getClustersAvailableForTier(canonical), [canonical]);
  const availableIds = useMemo(() => new Set(available.map((c) => c.id)), [available]);
  const isLarge = canonical === 'large';
  const isSmall = canonical === 'small';

  const selectedOptions = useMemo(
    () => available.filter((c) => selected.includes(c.id)),
    [available, selected]
  );

  const activeNudge = useMemo(() => {
    return CLUSTER_PAIRING_NUDGES.find((n) => {
      const key = `${n.from}-${n.to}`;
      if (dismissedNudges.has(key)) return false;
      if (!availableIds.has(n.to)) return false;
      return selected.includes(n.from) && !selected.includes(n.to);
    });
  }, [selected, dismissedNudges, availableIds]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  const patch = (updates) => {
    onChange?.(updates);
  };

  const toggleCluster = (clusterId) => {
    const id = Number(clusterId);
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    const nextFuel = resolveFuelSpecialistTypes(canonical, next, fuelSpecialistTypes);
    patch({ selectedClusters: next, fuelSpecialistTypes: nextFuel });
  };

  const removeCluster = (clusterId) => {
    const id = Number(clusterId);
    const next = selected.filter((x) => x !== id);
    const nextFuel = resolveFuelSpecialistTypes(canonical, next, fuelSpecialistTypes);
    patch({ selectedClusters: next, fuelSpecialistTypes: nextFuel });
  };

  const toggleFuelType = (fuelKey) => {
    const set = new Set(fuelSpecialistTypes);
    if (set.has(fuelKey)) set.delete(fuelKey);
    else set.add(fuelKey);
    patch({
      fuelSpecialistTypes: resolveFuelSpecialistTypes(canonical, selected, [...set]),
    });
  };

  useEffect(() => {
    if (!selected.includes(10)) return;
    const resolved = resolveFuelSpecialistTypes(canonical, selected, fuelSpecialistTypes);
    const current = JSON.stringify(fuelSpecialistTypes || []);
    const next = JSON.stringify(resolved);
    if (current !== next) {
      patch({ fuelSpecialistTypes: resolved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical, selected.join(','), fuelSpecialistTypes.join(',')]);

  const applyNudge = () => {
    if (!activeNudge) return;
    const next = normalizeIds([...selected, activeNudge.to]);
    patch({ selectedClusters: next });
    setDismissedNudges((s) => new Set(s).add(`${activeNudge.from}-${activeNudge.to}`));
  };

  if (!canonical || canonical === 'custom_workshop' || canonical === 'transformer') {
    return null;
  }

  const summary = selected.length === 0
    ? 'Select service clusters'
    : selected.length === 1
      ? selectedOptions[0]?.label || '1 cluster selected'
      : `${selected.length} clusters selected`;

  return (
    <Wrap>
      <HeaderRow>
        <Label id="garage-cluster-label">Service clusters *</Label>
        {/* {isSmall && (
          <Hint>Minimum {MIN_CLUSTERS_SMALL} clusters — medium-only options are hidden for small garages</Hint>
        )} */}
        {canonical === 'medium' && (
          <Hint>Includes medium-tier clusters (Clutch, Body &amp; Paint)</Hint>
        )}
        {isLarge && (
          <Hint>All clusters available — opt out if you cannot service a category</Hint>
        )}
      </HeaderRow>

      <DropdownWrap ref={wrapRef}>
        <Trigger
          type="button"
          id="selectedClusters"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-labelledby="garage-cluster-label"
          onClick={() => setOpen((prev) => !prev)}
          $hasError={hasError}
          $open={open}
        >
          <TriggerText $placeholder={selected.length === 0}>{summary}</TriggerText>
          <Chevron $open={open} aria-hidden />
        </Trigger>

        {open && (
          <Menu role="listbox" aria-multiselectable="true" aria-label="Service clusters">
            {available.map((cluster) => {
              const checked = selected.includes(cluster.id);
              const isMediumOnly = cluster.tierFloor === 'medium';
              return (
                <Option
                  key={cluster.id}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  $active={checked}
                  onClick={() => toggleCluster(cluster.id)}
                >
                  <Check $checked={checked}>{checked ? <FaCheck size={10} /> : null}</Check>
                  <OptionText>
                    <span>{cluster.label}</span>
                    {isMediumOnly && canonical !== 'small' && (
                      <MediumBadge>Medium garage+</MediumBadge>
                    )}
                  </OptionText>
                </Option>
              );
            })}
          </Menu>
        )}
      </DropdownWrap>

      {selectedOptions.length > 0 && (
        <ChipRow>
          {selectedOptions.map((cluster) => (
            <Chip key={cluster.id}>
              {cluster.label}
              <ChipRemove
                type="button"
                aria-label={`Remove ${cluster.label}`}
                onClick={() => removeCluster(cluster.id)}
              >
                <FaTimes size={10} />
              </ChipRemove>
            </Chip>
          ))}
        </ChipRow>
      )}

      {selected.includes(10) && (
        isLarge ? (
          <FuelSection>
            <FuelLabel>Fuel specialist type *</FuelLabel>
            <FuelRow>
              <FuelChip
                type="button"
                $active={fuelSpecialistTypes.includes('cng_lpg')}
                onClick={() => toggleFuelType('cng_lpg')}
              >
                CNG / LPG
              </FuelChip>
              <FuelChip
                type="button"
                $active={fuelSpecialistTypes.includes('ev_hybrid')}
                onClick={() => toggleFuelType('ev_hybrid')}
              >
                EV / Hybrid
              </FuelChip>
            </FuelRow>
          </FuelSection>
        ) : (
          <FuelHint>
            CNG/LPG applies automatically for {canonical} garages. EV/Hybrid is only available for Large garages.
          </FuelHint>
        )
      )}

      {activeNudge && (
        <NudgeBanner>
          <FaInfoCircle aria-hidden />
          <span>{activeNudge.message}</span>
          <NudgeActions>
            <NudgeBtn type="button" onClick={applyNudge}>Add cluster</NudgeBtn>
            <NudgeBtn
              type="button"
              $muted
              onClick={() => setDismissedNudges((s) => new Set(s).add(`${activeNudge.from}-${activeNudge.to}`))}
            >
              Dismiss
            </NudgeBtn>
          </NudgeActions>
        </NudgeBanner>
      )}

      {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
      {!errorMessage && hasError ? <ErrorText>Select service clusters</ErrorText> : null}
    </Wrap>
  );
}

export function getDefaultClustersOnTierChange(companyType) {
  const tier = normalizeGarageTier(companyType);
  if (tier === 'large') return getDefaultClustersForTier('large');
  if (tier === 'custom_workshop' || tier === 'transformer') return [];
  return [];
}

/** Drop clusters not allowed for the target tier (e.g. medium-only on small garage). */
export function filterClustersForTier(companyType, clusterIds = []) {
  const allowed = new Set(getClustersAvailableForTier(companyType).map((c) => c.id));
  return normalizeIds(clusterIds).filter((id) => allowed.has(id));
}

const Wrap = styled.div`margin-top: 4px;`;
const HeaderRow = styled.div`
  display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px;
`;
const Label = styled.span`font-size: 12px; font-weight: 600; color: var(--pp-text);`;
const Hint = styled.span`font-size: 11px; color: var(--pp-muted); line-height: 1.4;`;

const DropdownWrap = styled.div`position: relative; width: 100%;`;
const Trigger = styled.button`
  display: flex; align-items: center; width: 100%; min-height: 44px;
  padding: 11px 40px 11px 12px; border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : p.$open ? 'var(--pp-brand)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn); font-size: 14px; color: var(--pp-text);
  background: var(--pp-surface); cursor: pointer; text-align: left; position: relative;
  &:focus { outline: none; border-color: ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-brand)')}; }
`;
const TriggerText = styled.span`
  flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: ${(p) => (p.$placeholder ? 'var(--pp-muted)' : 'var(--pp-text)')};
`;
const Chevron = styled(FaChevronDown)`
  position: absolute; right: 14px; top: 50%;
  transform: translateY(-50%) rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  color: var(--pp-muted); font-size: 12px; transition: transform 0.2s ease; pointer-events: none;
`;
const Menu = styled.div`
  position: absolute; z-index: 20; top: calc(100% + 4px); left: 0; right: 0;
  max-height: 260px; overflow-y: auto; background: var(--pp-surface);
  border: 1px solid var(--pp-border); border-radius: var(--pp-radius-btn);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); padding: 4px;
`;
const Option = styled.button`
  display: flex; align-items: flex-start; gap: 10px; width: 100%; padding: 10px;
  border: none; border-radius: 6px; background: ${(p) => (p.$active ? 'var(--pp-brand-bg)' : 'transparent')};
  color: var(--pp-text); font-size: 13px; text-align: left; cursor: pointer;
  &:hover { background: var(--pp-brand-bg); }
`;
const Check = styled.span`
  width: 18px; height: 18px; border-radius: 4px; flex-shrink: 0; margin-top: 1px;
  border: 1.5px solid ${(p) => (p.$checked ? 'var(--pp-brand)' : 'var(--pp-border)')};
  background: ${(p) => (p.$checked ? 'var(--pp-brand)' : 'var(--pp-surface)')};
  color: #fff; display: inline-flex; align-items: center; justify-content: center;
`;
const OptionText = styled.span`
  display: flex; flex-direction: column; gap: 3px; align-items: flex-start;
`;
const MediumBadge = styled.span`
  font-size: 10px; font-weight: 600; color: #1d4ed8; background: #eff6ff;
  border: 1px solid #bfdbfe; border-radius: 999px; padding: 1px 8px;
`;

const ChipRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px;`;
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px 4px 10px;
  border-radius: 999px; font-size: 11px; font-weight: 500;
  background: var(--pp-brand-bg); border: 1px solid var(--pp-brand); color: var(--pp-text);
`;
const ChipRemove = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  border: none; background: transparent; color: var(--pp-muted); cursor: pointer; padding: 0;
  &:hover { color: var(--pp-text); }
`;

const FuelSection = styled.div`margin-top: 12px;`;
const FuelLabel = styled.p`font-size: 11px; font-weight: 600; color: var(--pp-text); margin: 0 0 6px;`;
const FuelHint = styled.p`
  margin: 12px 0 0; padding: 10px 12px; border-radius: 8px; font-size: 11px; line-height: 1.45;
  color: var(--pp-muted); background: var(--pp-brand-bg); border: 1px solid var(--pp-border);
`;
const FuelRow = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const FuelChip = styled.button`
  font-size: 11px; padding: 6px 12px; border-radius: 999px; cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? 'var(--pp-brand)' : 'var(--pp-border)')};
  background: ${(p) => (p.$active ? 'var(--pp-brand-bg)' : 'var(--pp-surface)')};
  color: var(--pp-text); opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
`;

const NudgeBanner = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 10px; padding: 10px 12px;
  border-radius: 8px; background: #fff8eb; border: 1px solid #fcd34d; font-size: 12px; color: #92400e;
  svg { flex-shrink: 0; }
`;
const NudgeActions = styled.div`display: flex; gap: 6px; margin-left: auto;`;
const NudgeBtn = styled.button`
  font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 6px; cursor: pointer;
  border: 1px solid ${(p) => (p.$muted ? '#d1d5db' : '#f59e0b')};
  background: ${(p) => (p.$muted ? '#fff' : '#fbbf24')};
  color: ${(p) => (p.$muted ? '#6b7280' : '#78350f')};
`;
const ErrorText = styled.p`font-size: 11px; color: var(--pp-error-text); margin: 6px 0 0;`;
